import React, { useState, useEffect, useMemo } from 'react';
import Clock from '@mui/icons-material/AccessTime';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  TextField,
  Drawer
} from '@mui/material';
import { auth, db } from '../firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, updateDoc, increment, collection } from 'firebase/firestore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import LatexRenderer from './LatexRenderer';
import { getDifficultyLevelUI, marksFor, examPatternOf } from '../utils/examDifficulty';

const parseQType = (qTypeId) => {
  const type = Number(qTypeId);
  switch (type) {
    case 1: return String.raw`$\textbf{MCQ}$`;
    case 2: return String.raw`$\textbf{Match the columns I and II}$`;
    case 3: return String.raw`$\textbf{Assertion}$`;
    case 4: return String.raw`$\textbf{Reasoning}$`;
    case 5: return String.raw`$\textbf{Numerical}$`;
    case 6: return String.raw`$\textbf{Fill in the Blanks}$`;
    case 7: return String.raw`$\textbf{Image Based}$`;
    default: return String.raw`$\textbf{Unknown Type}$`;
  }
};

function TestWindow({ examSettings, onFinish, onLeave }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const allQuestions = useMemo(() => {
    if (!examSettings?.subjects) return [];
    let questions = [];
    Object.entries(examSettings.subjects).forEach(([subjectName, questionList]) => {
      const list = Array.isArray(questionList) ? questionList : [];
      list.forEach((q, idx) => {
        const dataObj = typeof q.question === 'object' && q.question !== null ? q.question : q;
        let normalizedOptions = [];

        const isImageUrl = (s) => (typeof s === 'string' && s.startsWith('http') && (s.match(/\.(jpeg|jpg|gif|png|webp)/i) || s.includes('firebasestorage')));
        const wrapIfImage = (s) => isImageUrl(s) ? `![Option Image](${s})` : s;

        const findOptionsInObj = (obj) => {
          if (!obj || typeof obj !== 'object') return null;
          const prefixes = ['op', 'option', 'Option', 'Opt', 'opt', 'choice', 'Choice'];
          for (const p of prefixes) {
            if (obj[p + '1'] !== undefined) return [obj[p + '1'], obj[p + '2'], obj[p + '3'], obj[p + '4']];
            if (obj[p + ' 1'] !== undefined) return [obj[p + ' 1'], obj[p + ' 2'], obj[p + ' 3'], obj[p + ' 4']];
            if (obj[p + '_1'] !== undefined) return [obj[p + '_1'], obj[p + '_2'], obj[p + '_3'], obj[p + '_4']];
            if (obj[p + 'A'] !== undefined) return [obj[p + 'A'], obj[p + 'B'], obj[p + 'C'], obj[p + 'D']];
            if (obj[p + 'a'] !== undefined) return [obj[p + 'a'], obj[p + 'b'], obj[p + 'c'], obj[p + 'd']];
          }
          if (obj.A !== undefined && obj.B !== undefined) return [obj.A, obj.B, obj.C, obj.D];
          if (obj.a !== undefined && obj.b !== undefined) return [obj.a, obj.b, obj.c, obj.d];
          if (obj['1'] !== undefined && obj['2'] !== undefined) return [obj['1'], obj['2'], obj['3'] || '', obj['4'] || ''];
          return null;
        };

        if (Array.isArray(dataObj.options)) {
          normalizedOptions = dataObj.options.map(wrapIfImage);
        } else if (Array.isArray(dataObj.choices)) {
          normalizedOptions = dataObj.choices.map(wrapIfImage);
        } else if (dataObj.op1 || dataObj.option1 || dataObj.A) {
          const ops = [
            dataObj.op1 || dataObj.option1 || dataObj.A,
            dataObj.op2 || dataObj.option2 || dataObj.B,
            dataObj.op3 || dataObj.option3 || dataObj.C,
            dataObj.op4 || dataObj.option4 || dataObj.D
          ];
          normalizedOptions = ops.filter(o => o !== undefined).map(wrapIfImage);
        } else {
          const nested = findOptionsInObj(dataObj.options);
          const topLevel = findOptionsInObj(dataObj);
          const found = (nested || topLevel || []).filter(v => v !== undefined);

          if (found.length >= 2) {
            normalizedOptions = found.map(wrapIfImage);
          } else {
            const knownKeys = ['question', 'questiontext', 'text', 'answer', 'explanation', 'id', 'qcode', 'qtype_id', 'level_id', 'topic_id', 'supporting_picture', 'image_url', 'chapter_id', 'subtopic_id', 'created_at', 'updated_at', 'year', 'col1', 'col2', 'column1', 'column2', 'columni', 'columnii', 'assertion', 'reason', 'difficulty', 'type_id'];
            const potKeys = Object.keys(dataObj).filter(k => !knownKeys.includes(k.toLowerCase()) && typeof dataObj[k] === 'string' && dataObj[k].trim() !== '').sort();
            if (potKeys.length >= 2) {
              normalizedOptions = potKeys.map(k => wrapIfImage(dataObj[k]));
            } else {
              normalizedOptions = [`[Missing Options]`];
            }
          }
        }

        const baseQText = dataObj.question_text || dataObj.questionText || dataObj.question || dataObj.text || dataObj.name || q.question_text || q.questionText || q.question || "";
        const qtype_id = q.qtype_id || dataObj.qtype_id || q.type_id || dataObj.type_id;
        
        const ensureArray = (v) => {
          if (Array.isArray(v)) return v;
          if (v && typeof v === 'object') return Object.values(v);
          if (typeof v === 'string') {
            return v.split(/<br\s*\/?>|\n/gi).map(s => s.trim()).filter(s => s !== '');
          }
          return [];
        };

        const imgUrl = dataObj.supporting_picture || dataObj.question_image || dataObj.image_url || q.supporting_picture || q.image_url;
        const imageHeader = (imgUrl && imgUrl.trim() !== '' && imgUrl !== '""') ? `![Question Image](${imgUrl.trim()})\n\n` : '';

        const label = (t) => `$\\textbf{${t}}$`;
        const c1 = dataObj.col1 || dataObj.column1 || dataObj.columnI || dataObj.left || dataObj.L || q.col1 || q.column1 || q.columnI;
        const c2 = dataObj.col2 || dataObj.column2 || dataObj.columnII || dataObj.right || dataObj.R || q.col2 || q.column2 || q.columnII;
        // Assertion (qtype 3) and Statement (qtype 4) questions keep their two statements in col1/col2.
        // Some are mistyped and are really match-the-following, which have a second row marker like "(B)".
        const hasRows = /\(B\)|\(ii\)/i.test(String(c1 || ''));
        const twoTextColumns = typeof c1 === 'string' && typeof c2 === 'string' && c1.trim() && c2.trim();

        let qText = baseQText;
        if (dataObj.assertion && dataObj.reason) {
          qText = `${baseQText}\n\n${label('Assertion (A):')} ${dataObj.assertion}\n\n${label('Reason (R):')} ${dataObj.reason}`;
        } else if (twoTextColumns && !hasRows && Number(qtype_id) === 3) {
          qText = `${baseQText}\n\n${label('Assertion (A):')} ${c1}\n\n${label('Reason (R):')} ${c2}`;
        } else if (twoTextColumns && !hasRows && Number(qtype_id) === 4) {
          qText = `${baseQText}\n\n${label('Statement A:')} ${c1}\n\n${label('Statement B:')} ${c2}`;
        } else if (twoTextColumns) {
          // Whole columns, like the admin app: splitting on line breaks cuts multi-line equations apart
          qText = `${baseQText}\n\n${label('Column I')}\n${c1}\n\n${label('Column II')}\n${c2}`;
        } else {
          let col1 = ensureArray(c1);
          let col2 = ensureArray(c2);

          if (col1.length === 0) {
            const rows = [];
            for(let i=1; i<=10; i++) {
              const r = dataObj[`row${i}`] || q[`row${i}`];
              if (r) rows.push(r); else break;
            }
            if (rows.length > 0) {
              col1 = rows.map(r => r.col1 || r.left || r.L || "");
              col2 = rows.map(r => r.col2 || r.right || r.R || "");
            }
          }

          if (col1.length > 0) {
            const matchRows = col1.map((left, idx) => {
              const right = col2[idx] || "";
              const needsLeftLabel = !/^[A-Z1-9][.)]/.test(left);
              const needsRightLabel = !/^[A-Z1-9P-Z][.)]/.test(right);
              const lPrefix = needsLeftLabel ? `(${String.fromCharCode(65 + idx)}) ` : "";
              const rPrefix = needsRightLabel ? `(${idx + 1}) ` : "";
              return `${lPrefix}${left} \u00A0\u00A0\u00A0\u00A0 --- \u00A0\u00A0\u00A0\u00A0 ${rPrefix}${right}`;
            }).join('\n');
            qText = `${baseQText}\n\n$\\textbf{Column I}$ \u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0 $\\textbf{Column II}$\n${matchRows}`;
          }
        }

        qText = `${imageHeader}${qText.trim()}` || "[Question content missing]";

        const level_id = q.level_id || dataObj.level_id || q.difficulty || dataObj.difficulty;

        questions.push({
          ...q,
          level_id,
          qtype_id,
          parsedQuestionText: qText,
          options: normalizedOptions,
          subjectName,
          indexInSubject: idx + 1,
          globalIndex: questions.length
        });
      });
    });
    return questions;
  }, [examSettings]);

  const subjects = useMemo(() => Object.keys(examSettings?.subjects || {}), [examSettings]);
  const examId = examSettings?.id || examSettings?.docId || 'default';

  const [currentSubject, setCurrentSubject] = useState(() => {
    return localStorage.getItem(`test_subject_${examId}`) || subjects[0] || '';
  });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    return Number(localStorage.getItem(`test_qIdx_${examId}`)) || 0;
  });
  const [timeLeft, setTimeLeft] = useState(() => {
    const saved = localStorage.getItem(`test_time_${examId}`);
    return saved ? Number(saved) : (examSettings?.duration || 180) * 60;
  });
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem(`test_answers_${examId}`);
    return saved ? JSON.parse(saved) : {};
  });
  const [status, setStatus] = useState(() => {
    const saved = localStorage.getItem(`test_status_${examId}`);
    return saved ? JSON.parse(saved) : {};
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  const clearProgress = React.useCallback(() => {
    ['subject', 'qIdx', 'time', 'answers', 'status'].forEach(k => localStorage.removeItem(`test_${k}_${examId}`));
    localStorage.removeItem('examSettings'); // Clear from App.jsx storage too to avoid loop
  }, [examId]);

  // Leaving mid-exam always goes through the exit dialog: browser Back is intercepted, and closing or
  // reloading the tab gets the browser's own "Leave site?" warning (browsers don't allow custom text there).
  useEffect(() => {
    const trap = () => window.history.pushState({ examGuard: true }, '', window.location.href);
    const onBack = () => { trap(); setShowExitModal(true); };
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    trap();
    window.addEventListener('popstate', onBack);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('popstate', onBack);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(`test_subject_${examId}`, currentSubject);
  }, [currentSubject, examId]);

  useEffect(() => {
    localStorage.setItem(`test_qIdx_${examId}`, currentQuestionIndex.toString());
  }, [currentQuestionIndex, examId]);

  useEffect(() => {
    localStorage.setItem(`test_time_${examId}`, timeLeft.toString());
  }, [timeLeft, examId]);

  useEffect(() => {
    localStorage.setItem(`test_answers_${examId}`, JSON.stringify(answers));
  }, [answers, examId]);

  useEffect(() => {
    localStorage.setItem(`test_status_${examId}`, JSON.stringify(status));
  }, [status, examId]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => setCurrentUser(user));
    return () => unsubscribe();
  }, []);

  const filteredQuestions = useMemo(() => allQuestions.filter(q => q.subjectName === currentSubject), [allQuestions, currentSubject]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')} MIN`;
  };

  const currentQuestion = allQuestions[currentQuestionIndex];

  const handleOptionSelect = (optionIdx) => {
    setAnswers(prev => ({ ...prev, [currentQuestionIndex]: optionIdx }));
    setStatus(prev => ({ ...prev, [currentQuestionIndex]: 'answered' }));
  };

  const handleMarkReview = () => {
    setStatus(prev => ({ ...prev, [currentQuestionIndex]: 'review' }));
    handleNext();
  };

  const handleClear = () => {
    setAnswers(prev => {
      const newAns = { ...prev };
      delete newAns[currentQuestionIndex];
      return newAns;
    });
    setStatus(prev => ({ ...prev, [currentQuestionIndex]: 'visited' }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < allQuestions.length - 1) {
      const nextIdx = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIdx);
      setCurrentSubject(allQuestions[nextIdx].subjectName);
      if (!status[nextIdx]) setStatus(prev => ({ ...prev, [nextIdx]: 'visited' }));
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      const prevIdx = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIdx);
      setCurrentSubject(allQuestions[prevIdx].subjectName);
    }
  };

  const handleQuestionSelect = (globalIdx) => {
    setCurrentQuestionIndex(globalIdx);
    if (!status[globalIdx]) setStatus(prev => ({ ...prev, [globalIdx]: 'visited' }));
  };

  const handleSubmit = () => setShowSubmitModal(true);

  const handleConfirmSubmit = React.useCallback(async () => {
    setShowSubmitModal(false);
    setIsSubmitting(true);
    try {
      const resultId = Date.now().toString();
      let correctCount = 0, wrongCount = 0, unansweredCount = 0;
      const studentAnswersMap = {};
      const evaluatedQuestions = [];

      allQuestions.forEach((q, index) => {
        const studentAnsIdx = answers[index];
        studentAnswersMap[q.id || index] = studentAnsIdx !== undefined ? studentAnsIdx : null;
        let correctAns = q.correctAnswer ?? q.question?.correctAnswer ?? q.answer ?? q.question?.answer ?? q.correct ?? q.question?.correct;
        let isCorrect = false;
        if (studentAnsIdx !== undefined && studentAnsIdx !== null && studentAnsIdx !== "") {
          const isStringAnswer = typeof studentAnsIdx === 'string';
          
          if (isStringAnswer) {
            if (String(correctAns).trim().toLowerCase() === studentAnsIdx.trim().toLowerCase()) {
              isCorrect = true;
              correctCount++;
            } else {
              wrongCount++;
            }
          } else {
            const charLabel = String.fromCharCode(65 + studentAnsIdx);
            const isNumericMatch = String(correctAns) === String(studentAnsIdx + 1) || (String(correctAns) === "0" && studentAnsIdx === 0);
            const isTextMatch = String(correctAns).toUpperCase() === charLabel || String(correctAns) === String(q.options[studentAnsIdx]);
            
            if (isNumericMatch || isTextMatch) {
              isCorrect = true;
              correctCount++;
            } else {
              wrongCount++;
            }
          }
        } else {
          unansweredCount++;
        }
        evaluatedQuestions.push({ ...q, studentAnswerIndex: studentAnsIdx, isCorrect, correctAnswerValue: correctAns });
      });

      const marks = marksFor(examSettings);
      const finalScore = correctCount * marks.correct + wrongCount * marks.wrong;
      
      const isCreateOwn = examSettings?.type === 'create_own_exam';
      const examType = isCreateOwn ? "create_own_exam" : "scheduled_exam";
      const resultPayload = {
        resultId,
        examId: examSettings?.id || examSettings?.docId || `immediate_${resultId}`,
        // auth.currentUser as a fallback: the component's own copy can still be loading on a fast submit
        userId: currentUser?.uid || auth.currentUser?.uid || "anonymous",
        type: examType,
        examType,
        templateId: examSettings?.templateId || "",
        class: examSettings?.class || "",
        subject: examSettings?.subject || "",
        chapter: examSettings?.chapter || "",
        topic: examSettings?.topic || "",
        totalQuestions: allQuestions.length,
        correct: correctCount,
        wrong: wrongCount,
        unattempted: unansweredCount,
        score: finalScore,
        maxScore: allQuestions.length * marks.correct,
        examPattern: examPatternOf(examSettings),
        answers: studentAnswersMap,
        studentAnswers: studentAnswersMap,
        evaluatedQuestions: evaluatedQuestions.map(q => ({
          id: q.id || "",
          subjectName: q.subjectName || "",
          parsedQuestionText: q.parsedQuestionText || "",
          options: q.options || [],
          studentAnswerIndex: q.studentAnswerIndex ?? null,
          isCorrect: q.isCorrect ?? false,
          correctAnswerValue: q.correctAnswerValue ?? null,
          explanation: q.explanation || q.question?.explanation || "",
          level_id: q.level_id ?? null,
          qtype_id: q.qtype_id ?? null
        })),
        submittedAt: new Date()
      };

      await setDoc(doc(collection(db, 'results'), resultId), resultPayload);
      // Scheduled exams keep a running attempt count for the admin app (rules allow only +1 here).
      // Not awaited: a failed count must never hold up the student's result.
      if (examSettings?.docId) updateDoc(doc(db, 'exams', String(examSettings.docId)), { attempts: increment(1) }).catch(() => {});
      
      clearProgress();
      
      onFinish && onFinish({ ...resultPayload, evaluatedQuestions });
    } catch (error) {
      console.error(error);
      alert("Error submitting test: " + (error.message || error));
      setIsSubmitting(false);
    }
  }, [allQuestions, answers, currentUser, examSettings, onFinish, clearProgress]);

  useEffect(() => {
    if (timeLeft <= 0) {
      handleConfirmSubmit();
      return;
    }
    const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, handleConfirmSubmit]);

  const handleSubjectChange = (subjectName) => {
    setCurrentSubject(subjectName);
    const firstIdx = allQuestions.findIndex(q => q.subjectName === subjectName);
    if (firstIdx !== -1) {
      setCurrentQuestionIndex(firstIdx);
      if (!status[firstIdx]) setStatus(prev => ({ ...prev, [firstIdx]: 'visited' }));
    }
  };

  useEffect(() => {
    if (allQuestions.length > 0 && !status[0]) {
      setStatus(prev => ({ ...prev, 0: 'visited' }));
    }
  }, [allQuestions, status]);

  const getQuestionStatus = (idx) => status[idx] || 'notVisited';

  const renderSidebarContent = () => (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mb: '24px' }}>
        <Box component="img" src="/educonnect_logo.jpg" alt="Educonnect" sx={{ height: '44px', width: 'auto', maxWidth: '100%', objectFit: 'contain', alignSelf: 'flex-start', mixBlendMode: 'multiply' }} />
        <Typography sx={{ fontSize: '15px', color: '#1d4ed8', fontWeight: 800 }}>{currentUser?.displayName || (currentUser?.isAnonymous ? 'Guest' : 'Student')}</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', mb: '20px' }}>
        {[
          { label: 'Not Visited', color: '#334155' },
          { label: 'Un-answered', color: '#facc15' },
          { label: 'Answered', color: '#3b82f6' },
          { label: 'Review', color: '#94a3b8' }
        ].map((item, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', bgcolor: item.color }} />
            <Typography sx={{ fontSize: '11px', color: '#475569', fontWeight: 600 }}>{item.label}</Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ mb: '20px', bgcolor: '#bae6fd' }} />

      <Select fullWidth size="small" value={currentSubject} onChange={(e) => handleSubjectChange(e.target.value)}
        sx={{ bgcolor: '#ffffff', borderRadius: '6px', fontSize: '13px', fontWeight: 600, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#bae6fd' } }}>
        {subjects.map(sub => <MenuItem key={sub} value={sub} sx={{ fontSize: '13px' }}>
          <LatexRenderer text={sub} />
        </MenuItem>)}
      </Select>

      <Box sx={{ flex: 1, overflowY: 'auto', pr: '8px', mt: '20px', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#bae6fd' } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', justifyItems: 'center', pb: '16px' }}>
          {filteredQuestions.map((q) => {
            const qStatus = getQuestionStatus(q.globalIndex);
            let bgColor = '#334155', color = '#ffffff';
            if (qStatus === 'answered') bgColor = '#3b82f6';
            else if (qStatus === 'visited') { bgColor = '#facc15'; color = '#854d0e'; }
            else if (qStatus === 'review') bgColor = '#94a3b8';

            return (
              <Box key={q.globalIndex} onClick={() => {
                handleQuestionSelect(q.globalIndex);
                if (isMobile) setSidebarOpen(false);
              }}
                sx={{
                  width: '34px', height: '34px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', fontWeight: 700, cursor: 'pointer', bgcolor: bgColor, color: color,
                  transition: '0.2s', '&:hover': { opacity: 0.8, transform: 'scale(1.1)' }
                }}>
                {q.indexInSubject}
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );

  const handleAction = (type) => {
    if (type === 'review') handleMarkReview();
    if (type === 'clear') handleClear();
    if (type === 'prev') handlePrev();
    if (type === 'next') handleNext();
  };

  const attemptedCount = Object.values(status).filter(s => s === 'answered').length;
  const reviewCount = Object.values(status).filter(s => s === 'review').length;
  const unattemptedCount = allQuestions.length - attemptedCount;

  if (!currentQuestion) return <Box sx={{ p: '32px', textAlign: 'center' }}>No questions available.</Box>;

  const timer = (
    <Box sx={{ display: 'flex', alignItems: 'center', color: '#ef4444', flexShrink: 0 }}>
      <Clock sx={{ fontSize: '20px', mr: '8px' }} />
      <Typography sx={{ fontWeight: 700, fontSize: { xs: '15px', md: '16px' } }}>{formatTime(timeLeft)}</Typography>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      bgcolor: '#f8fafc',
      height: { xs: '100dvh', md: '100vh' },
      width: '100vw',
      overflow: 'hidden'
    }}>
      {isSubmitting && (
        <Box sx={{
          position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
          color: '#ffffff', fontSize: '20px', fontWeight: 800
        }}>
          Evaluating and Submitting Test...
        </Box>
      )}

      <Dialog open={showSubmitModal} onClose={() => setShowSubmitModal(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <Box sx={{
          bgcolor: '#f8fafc',
          p: '32px 24px', textAlign: 'center', position: 'relative',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <IconButton onClick={() => setShowSubmitModal(false)} sx={{ position: 'absolute', top: '8px', right: '8px', color: '#1d4ed8' }}>
            <CloseIcon sx={{ fontSize: '20px' }} />
          </IconButton>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box component="img" src="/educonnect_logo.jpg" alt="Educonnect" sx={{ height: '65px', width: 'auto', mb: '16px', mixBlendMode: 'multiply' }} />
            <Typography sx={{ color: '#1e3a8a', fontWeight: 800, fontSize: '22px', mb: '4px' }}>Confirm Submission</Typography>
            <Typography sx={{ color: '#1d4ed8', fontSize: '13px', fontWeight: 600, opacity: 0.8 }}>Would you like to finish your exam now?</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ py: '24px', px: '32px' }}>
          {[
            { label: 'Time Left', value: formatTime(timeLeft), icon: <AccessTimeIcon sx={{ color: '#64748b' }} />, color: '#334155' },
            { label: 'Attempted', value: attemptedCount, icon: <CheckCircleOutlineIcon sx={{ color: '#16a34a' }} />, color: '#16a34a' },
            { label: 'Unattempted', value: unattemptedCount, icon: <RadioButtonUncheckedIcon sx={{ color: '#ca8a04' }} />, color: '#a16207' },
            { label: 'Review', value: reviewCount, icon: <FlagOutlinedIcon sx={{ color: '#dc2626' }} />, color: '#dc2626' }
          ].map((stat, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: '16px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Box sx={{ display: 'flex', '& svg': { fontSize: 20 } }}>{stat.icon}</Box>
                <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '14px' }}>{stat.label}:</Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, color: stat.color, fontSize: '14px' }}>{stat.value}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: '24px', pt: '0px', gap: '8px' }}>
          <Button onClick={() => setShowSubmitModal(false)} sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, px: '24px', textTransform: 'none', fontSize: '14px' }}>Cancel</Button>
          <Button onClick={handleConfirmSubmit} variant="contained" sx={{ bgcolor: '#1d4ed8', fontWeight: 600, px: '32px', textTransform: 'none', fontSize: '14px' }}>Submit</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showExitModal} onClose={() => setShowExitModal(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <Box sx={{
          bgcolor: '#dc2626',
          p: '24px', textAlign: 'center', position: 'relative'
        }}>
          <IconButton onClick={() => setShowExitModal(false)} sx={{ position: 'absolute', top: '8px', right: '8px', color: '#ffffff' }}>
            <CloseIcon sx={{ fontSize: '20px' }} />
          </IconButton>
          <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: '18px' }}>
            {attemptedCount > 0 ? 'Leave and submit the exam?' : 'Leave the exam?'}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: '20px !important', px: '28px' }}>
          <Typography sx={{ color: '#334155', fontSize: '14px', lineHeight: 1.6 }}>
            {attemptedCount > 0
              ? `You've answered ${attemptedCount} of ${allQuestions.length} questions. If you leave now, the exam will be submitted and these answers will be your score.`
              : "You haven't answered any questions yet. If you leave now, this attempt won't be saved."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '24px', pt: '8px', gap: '8px' }}>
          <Button onClick={() => setShowExitModal(false)} sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, px: '24px', textTransform: 'none', fontSize: '14px' }}>Stay in exam</Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowExitModal(false);
              if (attemptedCount > 0) {
                handleConfirmSubmit();
              } else {
                clearProgress();
                onLeave();
              }
            }}
            sx={{ bgcolor: '#ef4444', fontWeight: 600, px: '24px', textTransform: 'none', fontSize: '14px', '&:hover': { bgcolor: '#dc2626' } }}
          >
            {attemptedCount > 0 ? 'Submit & leave' : 'Leave exam'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ 
        width: '100%', 
        px: { xs: '16px', md: '60px' }, 
        py: { xs: '10px', md: '18px' }, 
        color: '#1e293b', 
        fontWeight: 800, 
        fontSize: { xs: '15px', md: '18px' }, 
        borderBottom: '1px solid #e2e8f0', 
        bgcolor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px'
      }}>
        <Box sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <LatexRenderer text={examSettings?.id || examSettings?.examName || ''} />
        </Box>
        {/* Phones: the timer lives in the top bar so the question gets the height */}
        {isMobile && timer}
      </Box>

      <Box sx={{
        display: 'flex',
        gap: { xs: '12px', md: '24px' },
        flex: 1,
        width: '100%',
        maxWidth: '1440px',
        overflow: 'hidden',
        px: { xs: '16px', md: '60px' },
        py: { xs: '12px', md: '24px' },
        flexDirection: isMobile ? 'column' : 'row',
        minHeight: 0
      }}>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: { xs: '12px', md: '16px' }, minHeight: 0 }}>
          <Paper sx={{ 
            p: { xs: '12px', md: '16px' }, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            borderRadius: '8px', 
            border: '1px solid #e2e8f0', 
            boxShadow: 'none',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            {!isMobile && timer}
            <Box sx={isMobile
              ? { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', width: '100%' }
              : { display: 'flex', gap: '8px' }}>
              {isMobile && (
                <Button
                  variant="outlined"
                  onClick={() => setSidebarOpen(true)}
                  sx={{
                    fontWeight: 600,
                    px: '8px',
                    textTransform: 'none',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  Questions
                </Button>
              )}
              <Button variant="outlined" color="error" onClick={() => setShowExitModal(true)} sx={{ fontWeight: 600, px: { xs: '8px', md: '24px' }, textTransform: 'none', borderRadius: '6px' }}>Exit</Button>
              <Button variant="contained" color="success" onClick={handleSubmit} sx={{ fontWeight: 600, px: { xs: '8px', md: '32px' }, textTransform: 'none', borderRadius: '6px' }}>Submit</Button>
            </Box>
          </Paper>

          <Paper sx={{ 
            p: { xs: '12px', sm: '16px', md: '24px' }, 
            flex: 1,
            borderRadius: '8px', 
            border: '1px solid #e2e8f0', 
            boxShadow: 'none', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: { xs: '12px', md: '24px' },
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Box sx={{ color: '#1d4ed8', fontWeight: 800, fontSize: { xs: '15px', md: '18px' } }}>
                  <LatexRenderer text={String.raw`$\textbf{Question ${currentQuestion.indexInSubject} / ${filteredQuestions.length}}$`} />
                </Box>
                <Box sx={{ color: '#64748b', fontSize: '18px', mx: '4px', display: { xs: 'none', sm: 'block' } }}>•</Box>
                <Box sx={{ color: '#1d4ed8', fontWeight: 600, fontSize: { xs: '13px', md: '16px' } }}>
                  <LatexRenderer text={currentQuestion.subjectName} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: '8px' }}>
                {[
                  { label: getDifficultyLevelUI(currentQuestion.level_id).label, color: getDifficultyLevelUI(currentQuestion.level_id).color },
                  { label: parseQType(currentQuestion.qtype_id), color: '#475569' }
                ].map((tag, i) => (
                  <Box key={i} sx={{ px: { xs: '8px', md: '12px' }, py: '4px', borderRadius: '16px', bgcolor: '#f8fafc', border: '1px solid #cbd5e1', color: tag.color, fontSize: '11px', fontWeight: 600 }}>
                    <LatexRenderer text={tag.label} />
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ 
              mb: { xs: '12px', md: '24px' }, 
              flex: 1, 
              overflowY: 'auto', 
              pr: '12px',
              minHeight: 0,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { bgcolor: '#f1f5f9' },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: '10px' }
            }}>
              <Typography sx={{ fontSize: { xs: '14px', md: '16px' }, color: '#334155', lineHeight: 1.6, mb: { xs: '16px', md: '32px' } }}>
                <LatexRenderer text={currentQuestion.parsedQuestionText} />
              </Typography>

              <Box>
                {Number(currentQuestion.qtype_id) === 5 || Number(currentQuestion.qtype_id) === 6 || (currentQuestion.options.length === 1 && currentQuestion.options[0] === '[Missing Options]') ? (
                  <TextField 
                    fullWidth 
                    variant="outlined" 
                    placeholder="Type your answer here..." 
                    value={answers[currentQuestionIndex] || ''} 
                    onChange={(e) => {
                      setAnswers(prev => ({ ...prev, [currentQuestionIndex]: e.target.value }));
                      setStatus(prev => ({ ...prev, [currentQuestionIndex]: e.target.value.trim() ? 'answered' : 'visited' }));
                    }}
                    sx={{ mt: 2, bgcolor: '#ffffff' }}
                  />
                ) : (
                  currentQuestion.options.map((opt, idx) => {
                    const isSelected = answers[currentQuestionIndex] === idx;
                    return (
                      <Box key={idx} onClick={() => handleOptionSelect(idx)}
                        sx={{
                          p: { xs: '10px 12px', md: '16px' }, mb: { xs: '8px', md: '12px' }, borderRadius: '8px', border: '1px solid',
                          borderColor: isSelected ? '#3b82f6' : '#cbd5e1',
                          bgcolor: isSelected ? '#eff6ff' : '#ffffff',
                          display: 'flex', alignItems: 'center', cursor: 'pointer',
                          transition: '0.2s', '&:hover': { bgcolor: isSelected ? '#eff6ff' : '#f8fafc' }
                        }}>
                        <Box sx={{
                          width: '28px', height: '28px', borderRadius: '50%', border: '1px solid',
                          borderColor: isSelected ? '#1d4ed8' : '#94a3b8',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          mr: '16px', fontSize: '13px', fontWeight: 600,
                          color: isSelected ? '#1d4ed8' : '#64748b',
                          bgcolor: isSelected ? '#dbeafe' : 'transparent',
                          flexShrink: 0
                        }}>
                          <LatexRenderer text={`$\\textbf{${String.fromCharCode(65 + idx)}}$`} />
                        </Box>
                        <Typography sx={{ fontSize: '14px', color: '#334155' }}>
                          <LatexRenderer text={opt && typeof opt === 'object' ? opt.text : opt} />
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: { xs: '10px', md: '16px' }, bgcolor: '#e2e8f0' }} />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              gap: { xs: '8px', md: '16px' }
            }}>
              <Box sx={{ display: 'flex', gap: { xs: '6px', md: '12px' } }}>
                <Button onClick={() => handleAction('review')}
                  sx={{ 
                    bgcolor: '#e0f2fe', 
                    color: '#0369a1', 
                    border: '1px solid #bae6fd', 
                    fontWeight: 600, 
                    fontSize: { xs: '11px', sm: '13px' }, 
                    textTransform: 'none', 
                    borderRadius: '6px', 
                    px: { xs: '8px', md: '16px' }, 
                    py: { xs: '6px', md: '8px' }, 
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#bae6fd' } 
                  }}
                >
                  {isMobile ? "Review & Next" : "Mark for Review & Next"}
                </Button>
                <Button onClick={() => handleAction('clear')} 
                  sx={{ 
                    color: '#64748b', 
                    fontSize: { xs: '11px', sm: '13px' }, 
                    fontWeight: 600, 
                    textTransform: 'none',
                    px: { xs: '6px', md: '12px' }
                  }}
                >
                  Clear
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: { xs: '6px', md: '8px' } }}>
                <IconButton onClick={handlePrev} disabled={currentQuestionIndex === 0} sx={{ bgcolor: '#f1f5f9', borderRadius: '6px', p: { xs: '6px', md: '8px' } }}><ChevronLeft sx={{ fontSize: { xs: '20px', md: '24px' } }} /></IconButton>
                <IconButton onClick={handleNext} sx={{ bgcolor: '#3b82f6', color: '#ffffff', borderRadius: '6px', p: { xs: '6px', md: '8px' }, '&:hover': { bgcolor: '#2563eb' } }}><ChevronRight sx={{ fontSize: { xs: '20px', md: '24px' } }} /></IconButton>
              </Box>
            </Box>
          </Paper>
        </Box>

        {!isMobile && (
          <Box sx={{ width: '300px', height: '100%' }}>
            <Paper sx={{ 
              p: '20px', 
              bgcolor: '#dcf1fb', 
              height: '100%', 
              borderRadius: '8px', 
              border: '1px solid #bae6fd', 
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {renderSidebarContent()}
            </Paper>
          </Box>
        )}

        {isMobile && (
          <Drawer
            anchor="right"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            PaperProps={{
              sx: {
                width: '280px',
                bgcolor: '#dcf1fb',
                height: '100%',
                p: '20px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }
            }}
          >
            {renderSidebarContent()}
          </Drawer>
        )}

      </Box>
    </Box>
  );
}

export default TestWindow;

